import type { Matter, MatterSubItemType } from "@/lib/types";
import type { MatterFile, MatterFileReference } from "@/lib/types/Matter";
import {
  File,
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileIcon as FilePdf,
  FileSpreadsheet,
  FileText,
  FileType,
  FileVideo,
  UserCheck,
} from "lucide-react";

export function getFileMoveConflictMessage(
  selectedItems: Pick<MatterFile, "id" | "name" | "idLocation">[],
  repositoryItems: Pick<MatterFile, "id" | "name" | "idLocation">[],
  targetFolderId: string,
): string | null {
  const destinationItems = repositoryItems.filter(
    (item) => (item.idLocation ?? "") === targetFolderId,
  );
  const destinationIds = new Set(destinationItems.map((item) => item.id));
  const destinationNames = new Set(
    destinationItems.map((item) => item.name.toLowerCase()).filter(Boolean),
  );
  const seenIds = new Set<string>();
  for (const item of selectedItems) {
    if (seenIds.has(item.id) || destinationIds.has(item.id)) continue;
    seenIds.add(item.id);
    const name = item.name.toLowerCase();
    if (!name) continue;
    if (destinationNames.has(name)) {
      return `Cannot move these items because more than one would be named "${item.name}". Rename the conflicting items and try again. No items were moved.`;
    }
    destinationNames.add(name);
  }
  return null;
}

export const MATTER_FILE_TAG_VALUES = [
  "matterFileContactIdentification",
  "matterFileInjury",
  "matterFilePropertyDamage",
] as const;

export type MatterFileTag = (typeof MATTER_FILE_TAG_VALUES)[number];

export const MATTER_FILE_TAG_LABELS: Record<MatterFileTag, string> = {
  matterFileContactIdentification: "Contact Identification",
  matterFileInjury: "Injury",
  matterFilePropertyDamage: "Property Damage",
};

export const MATTER_FILE_TAG_OPTIONS = MATTER_FILE_TAG_VALUES.map((value) => ({
  value,
  label: MATTER_FILE_TAG_LABELS[value],
}));

export const MERGE_SUPPORTED_MATTER_FILE_EXTENSIONS = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
] as const;

export const getMatterFileTagLabels = (tags?: MatterFile["tags"]): string[] =>
  (tags ?? []).flatMap((tag) =>
    tag in MATTER_FILE_TAG_LABELS ? [MATTER_FILE_TAG_LABELS[tag]] : [],
  );

export const isCriticalImage = (matterFile: Pick<MatterFile, "tags">) =>
  getMatterFileTagLabels(matterFile.tags).length > 0;

export const getMatterImageTypeMessage = (
  matterFile: Pick<MatterFile, "tags">,
) => getMatterFileTagLabels(matterFile.tags).join(", ");

export const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase() || "";

  if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(extension)) {
    return FileImage;
  } else if (extension === "pdf") {
    return FilePdf;
  } else if (["txt", "md", "rtf"].includes(extension)) {
    return FileText;
  } else if (["mp3", "wav", "ogg", "flac", "aac"].includes(extension)) {
    return FileAudio;
  } else if (
    ["mp4", "webm", "avi", "mov", "wmv", "flv", "mkv"].includes(extension)
  ) {
    return FileVideo;
  } else if (["xlsx", "xls", "csv", "ods"].includes(extension)) {
    return FileSpreadsheet;
  } else if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) {
    return FileArchive;
  } else if (
    [
      "js",
      "ts",
      "jsx",
      "tsx",
      "html",
      "css",
      "json",
      "xml",
      "py",
      "java",
      "php",
      "c",
      "cpp",
      "h",
    ].includes(extension)
  ) {
    return FileCode;
  } else if (["doc", "docx", "odt"].includes(extension)) {
    return FileType;
  } else if (extension === "vcf") {
    return UserCheck;
  }

  // Default icon if nothing matches
  return File;
};

export const getIsFileImage = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase() || "";

  return ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(
    extension,
  );
};

export const matterFileNameExistsInFolder = (
  matterFiles: Pick<MatterFile, "id" | "idLocation" | "name">[],
  folderId: string,
  fileName: string,
  excludeMatterFileId?: string,
) =>
  matterFiles.some(
    (file) =>
      (file.idLocation || "") === (folderId || "") &&
      file.name === fileName &&
      file.id !== excludeMatterFileId,
  );

export const getMatterFileNamesInFolder = (
  matterFiles: Pick<MatterFile, "idLocation" | "name">[],
  folderId: string,
) =>
  matterFiles
    .filter((file) => (file.idLocation || "") === (folderId || ""))
    .map((file) => file.name);

export const getFileNameParts = (fileName?: string) => {
  if (!fileName)
    return {
      fileName: "",
      fileExtension: "",
    };
  if (!fileName.includes(".")) {
    return {
      fileName: fileName,
      fileExtension: "",
    };
  }
  const fileNameParts = fileName.split(".");
  return {
    fileName: fileNameParts.slice(0, fileNameParts.length - 1).join("."),
    fileExtension: fileNameParts[fileNameParts.length - 1],
  };
};

// Current versions may change format while retaining their original display name.
export const getFileDocumentFormat = (file: {
  name: string;
  contentType?: string | null;
}) => {
  if (!file.contentType || file.contentType === "application/octet-stream")
    return getFileNameParts(file.name).fileExtension.toLowerCase();
  if (file.contentType === "application/pdf") return "pdf";
  if (
    file.contentType ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return "docx";
  return "";
};

export const ensureFileNameHasExtension = (
  fileName: string,
  extension?: string,
) => {
  const normalizedFileName = fileName.trim();
  const normalizedExtension = extension?.trim().replace(/^\.+/, "") ?? "";

  if (!normalizedFileName || !normalizedExtension) {
    return normalizedFileName;
  }

  const { fileExtension } = getFileNameParts(normalizedFileName);
  if (fileExtension.toLowerCase() === normalizedExtension.toLowerCase()) {
    return normalizedFileName;
  }

  return `${normalizedFileName}.${normalizedExtension}`;
};

export const getFileUploadContentType = (file: {
  name: string;
  type: string;
}) =>
  // Match the signed S3 content type even when the browser cannot identify G64X.
  file.name.toLowerCase().endsWith(".g64x")
    ? "application/octet-stream"
    : file.type;

export const isMergeSupportedMatterFile = (
  matterFile: Pick<MatterFile, "isFolder" | "name">,
) => {
  if (matterFile.isFolder) return false;

  const extension = getFileNameParts(
    matterFile.name,
  ).fileExtension.toLowerCase();

  return MERGE_SUPPORTED_MATTER_FILE_EXTENSIONS.includes(
    extension as (typeof MERGE_SUPPORTED_MATTER_FILE_EXTENSIONS)[number],
  );
};

export const buildUniqueFileName = (
  baseName: string,
  existingNames: string[],
) => {
  const normalizedBaseName = baseName.trim();
  if (!normalizedBaseName) {
    return baseName;
  }

  const normalizedExistingNames = new Set(
    existingNames.map((name) => name.toLowerCase()),
  );
  if (!normalizedExistingNames.has(normalizedBaseName.toLowerCase())) {
    return normalizedBaseName;
  }

  const { fileExtension, fileName } = getFileNameParts(normalizedBaseName);
  let index = 1;
  let candidate = `${fileName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;

  while (normalizedExistingNames.has(candidate.toLowerCase())) {
    index += 1;
    candidate = `${fileName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;
  }

  return candidate;
};

export const handlePreviewFile = (_fileName: string, fileUrl?: string) => {
  if (!fileUrl) {
    console.error("No URL available for download");
    return;
  }

  const previewWindow = window.open(fileUrl, "_blank", "noopener,noreferrer");

  if (previewWindow) {
    return;
  }

  const a = document.createElement("a");
  a.href = fileUrl;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// Canonical download URLs already carry the attachment filename. Let the
// browser stream large files instead of buffering the entire response as a Blob.
export const downloadFileLink = (fileName: string, fileUrl: string) => {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = fileName;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const handleDownload = async (
  fileName: string,
  fileUrl: string,
  throwOnError = false,
) => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok)
      throw new Error(
        "Unable to download this file. Please request a fresh download and try again.",
      );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    if (throwOnError) throw error;
    console.error("Download failed");
  }
};

/** Folder chain from repository root to the given folder (root → leaf). */
export const buildMatterFileFolderBreadcrumbs = (
  matterFiles: Pick<MatterFile, "id" | "idLocation" | "isFolder" | "name">[],
  currentFolderId: string,
) => {
  const folderMap = new Map(
    matterFiles.filter((file) => file.isFolder).map((file) => [file.id, file]),
  );

  const breadcrumbs: Pick<
    MatterFile,
    "id" | "idLocation" | "isFolder" | "name"
  >[] = [];
  let cursor = currentFolderId;

  while (cursor) {
    const folder = folderMap.get(cursor);
    if (!folder) {
      break;
    }
    breadcrumbs.unshift(folder);
    cursor = folder.idLocation || "";
  }

  return breadcrumbs;
};

export const mergeFilesAndReferenceId = (
  matter?: Matter,
  filterKey?: keyof MatterFileReference,
  filterId?: unknown,
) => {
  if (!matter || !filterKey || !filterId) return [];
  const references: (MatterFile & { fileReferenceId: string })[] = [];
  const matterFileReferences = matter.fileReferences;
  const matterFiles = matter.files;
  matterFileReferences
    .filter((reference) => reference[filterKey] === filterId)
    .forEach((reference) => {
      const file = matterFiles.find((file) => file.id === reference.fileId);
      if (file) {
        references.push({
          ...file,
          fileReferenceId: reference.id,
        });
      }
    });
  return references;
};

export const matterFileReferenceParentIdFieldBySubItemType: Partial<
  Record<MatterSubItemType, keyof MatterFileReference>
> = {
  vehicle: "vehicleId",
  "incident-report": "incidentReportId",
  insurance: "insuranceId",
  "medical-treatment": "medicalTreatmentId",
  "medical-treatment-bill": "medicalTreatmentBillId",
  "medical-treatment-appointment": "medicalTreatmentAppointmentId",
  "medical-treatment-record": "medicalTreatmentRecordId",
  "medical-treatment-plan": "medicalTreatmentPlanId",
  "medical-treatment-balance-confirmation":
    "medicalTreatmentBalanceConfirmationId",
  "settlement-demand": "settlementDemandId",
  "settlement-offer": "settlementOfferId",
  "settlement-counter": "settlementCounterId",
  expense: "expenseId",
  "lost-wage": "lostWagesId",
  lien: "lienId",
  "property-damage": "propertyDamageId",
  "signature-request": "signatureRequestId",
  "trust-check": "trustCheckId",
  "matter-litigation-date": "litigationDateId",
  "statute-of-limitations": "statuteOfLimitationsId",
  email: "emailId",
};

export const getMatterFileReferenceParentPayload = (subItem: {
  id: string;
  type: MatterSubItemType;
}): Partial<MatterFileReference> => {
  const parentIdField =
    matterFileReferenceParentIdFieldBySubItemType[subItem.type];

  return parentIdField ? { [parentIdField]: subItem.id } : {};
};

export function sortFiles<
  T extends {
    name: string;
    dateCreated: string;
    dateModified: string;
  },
>(items: readonly T[], sortKey: "name" | "dateCreated" | "dateModified"): T[] {
  const getTime = (value?: string) => (value ? new Date(value).getTime() : 0);

  const collator = new Intl.Collator(undefined, {
    sensitivity: "base",
    numeric: true,
  });

  return [...items].sort((a, b) => {
    switch (sortKey) {
      case "name":
        return collator.compare(a.name, b.name);
      case "dateCreated":
        return getTime(b.dateCreated) - getTime(a.dateCreated);
      case "dateModified":
      default:
        return getTime(b.dateModified) - getTime(a.dateModified);
    }
  });
}
